import {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {Footer} from '@common/ui/footer/footer';

const securityRows = [
  {
    title: (
      <>
        <span className="text-[#d8562a]">Encryption</span> in transit
      </>
    ),
    body: `Every file you upload or download is protected with TLS 1.3 encryption, so your data is never exposed as it moves between your device and our network.`,
    reverse: true,
    imageSrc: '/images/Page_security/Encryption_in_Transit.png',
    imageAlt: 'Encryption in transit',
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">Encryption</span> at rest
      </>
    ),
    body: `Files are encrypted with AES-256 before being stored, the same standard trusted by banks and governments.`,
    reverse: false,
    imageSrc: '/images/Page_security/Encryption_at_Rest.png',
    imageAlt: 'Encryption at rest',
  },
  {
    title: (
      <>
        <span className="font-['JetBrains_Mono',monospace] text-[#d8562a]">
          End-to-end encryption
        </span>{' '}
        <span className="font-['JetBrains_Mono',monospace]">(paid tiers)</span>
      </>
    ),
    body: `Upgrade to unlock zero-knowledge E2EE. Files are encrypted on your device before being uploaded to your storage, meaning you're the only one with the key. not us, not Storj, not anyone.`,
    reverse: true,
    imageSrc: '/images/Page_security/End-to-End_Encryption.png',
    imageAlt: 'End-to-end encryption',
  },
  {
    title: (
      <>
        <span className="font-['JetBrains_Mono',monospace] text-[#d8562a]">
          No
        </span>{' '}
        <span className="font-['JetBrains_Mono',monospace]">central file vault</span>
      </>
    ),
    body: `Traditional cloud storage stores your files in massive centralized data centers — attractive targets for attackers.

Our architecture doesn't work that way. Your files are split into encrypted pieces and scattered across thousands of nodes, so there's no single vault to breach and no complete copy sitting in one place.`,
    reverse: false,
    imageSrc: '/images/Page_security/No_Central_File_Vault.png',
    imageAlt: 'No central file vault',
  },
];

const IMAGE_FRAME_CLASS =
  'mx-auto h-[200px] w-[92%] object-contain object-center lg:h-[430px] lg:w-[90%]';

const IMAGE_WRAP_CLASS = 'w-full';

export function SecurityPage() {
  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen bg-[#f5f5f7] text-[#1a1a1a] dark:bg-[#14161a] dark:text-[#f3f4f6]">
        <SiteHeader active="features" />
        <div className="relative mx-auto w-full max-w-[1895px] px-[100px] pb-[24px] lg:pb-[25px] max-[767px]:px-[50px]">
          <section className="mx-auto mt-[56px] w-full max-w-[1565px] lg:mt-[170px]">
            <h1 className="font-['JetBrains_Mono',monospace] text-[34px] font-bold leading-[1.1] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[38px] lg:text-[60px] lg:leading-[1.08]">
              Security that actually means something.
            </h1>
            <p className="mt-[18px] text-[18px] font-bold leading-[1.36] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:mt-[30px] lg:text-[25px] lg:leading-[1.32]">
              <span className="italic text-[#d8562a]">AES-256</span>. The same
              standard used by banks, and militaries.
              <br />
              <br />
              <span className="italic text-[#d8562a]">
                Argon2id password hashing
              </span>
              . memory-hard and built to resist GPU and ASIC cracking attacks.
              <br />
              <br />
              Storage should be a <span className="italic text-[#d8562a]">utility</span>
              , not a surveillance product.
            </p>
            <Link
              to="/register"
              className="mt-[28px] inline-flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2b7e20] text-[17px] font-medium text-white transition-colors hover:bg-[#3c7d44] lg:mt-[40px]"
            >
              Try for free
            </Link>
          </section>

          <div className="mx-auto mt-[72px] grid w-full max-w-[1565px] grid-cols-1 gap-y-[80px] lg:mt-[110px] lg:gap-y-[132px]">
            {securityRows.map(section => (
              <section key={section.body}>
                <h2
                  className={`font-['Inter',sans-serif] text-[32px] font-bold leading-[1.15] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[36px] lg:text-[60px] lg:leading-[1.1] ${
                    section.reverse ? 'text-left lg:text-right' : 'text-left'
                  }`}
                >
                  {section.title}
                </h2>
                {section.imageSrc ? (
                  <div className="mt-[20px] grid grid-cols-1 items-center gap-[24px] lg:mt-[30px] lg:gap-[36px] lg:grid-cols-2">
                    <div
                      className={`${
                        section.reverse ? 'lg:order-1' : 'lg:order-2'
                      } ${IMAGE_WRAP_CLASS}`}
                    >
                      <img
                        src={section.imageSrc}
                        alt={section.imageAlt}
                        className={IMAGE_FRAME_CLASS}
                      />
                    </div>
                    <p
                      className={`${
                        section.reverse ? 'lg:order-2' : 'lg:order-1'
                      } max-w-[646px] whitespace-pre-line text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]`}
                    >
                      {section.body}
                    </p>
                  </div>
                ) : (
                  <p className="mt-[20px] whitespace-pre-line text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:mt-[30px] lg:text-[25px] lg:leading-[1.35]">
                    {section.body}
                  </p>
                )}
              </section>
            ))}
          </div>

          <section className="mx-auto mt-[84px] w-full max-w-[1565px] text-center lg:mt-[130px]">
            <h2 className="font-['Inter',sans-serif] text-[36px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[42px] lg:text-[60px] lg:leading-[1.16]">
              Want to give it a try?
            </h2>
            <p className="mx-auto mt-[14px] max-w-[629px] text-[18px] font-normal leading-[1.4] text-[#1a1a1a] dark:text-[#e4e8ef] sm:text-[22px] lg:mt-[20px] lg:text-[30px] lg:leading-[1.35]">
              Whether you choose a free account, paid or just passing by, we have
              something for you.
            </p>
            <Link
              to="/register"
              className="mt-[22px] inline-flex h-[64px] items-center justify-center rounded-[30px] bg-[#2b7e20] px-[30px] text-[21px] font-medium text-white transition-colors hover:bg-[#3c7d44] lg:mt-[32px] lg:h-[108px] lg:rounded-[43px] lg:px-[44px] lg:text-[30px]"
            >
              Try for free
            </Link>
          </section>

          <Footer className="mt-[44px] lg:mt-[120px] lg:min-h-[190px]" />
        </div>
      </main>
    </Fragment>
  );
}
